import {
  ETextParamType_SceneItem,
  ETextParamType_OnARDetectedImage,
  ETextParamType_OnARFoundWayspot
} from '../logics/const';
import {getWebGL10Toggle} from '../logics/dataOps';
import {hel_setText} from '../logics/moduleCaller';

let g_AR_BGItem: string = '';
let g_AR_CameraItem: string = '';
let g_AR_Enabled_WorldTracking = true;
let g_AR_Enabled_ImageTracking = false;
let g_AR_Enabled_VPS = false;
let g_AR_imageTargets: Array<string> = [];

declare const XR8: any;
declare const XRExtras: any;

export const Load8thwallScripts = async (settings: any) => {
  const apiKey: string = settings.apiKey;
  g_AR_BGItem = settings.bgItem;
  g_AR_CameraItem = settings.cameraItem;

  // 必須情報のチェック
  if (
    apiKey === undefined ||
    apiKey === null ||
    apiKey === '' ||
    g_AR_BGItem === undefined ||
    g_AR_BGItem === null ||
    g_AR_BGItem === '' ||
    g_AR_CameraItem === undefined ||
    g_AR_CameraItem === null ||
    g_AR_CameraItem === ''
  ) {
    return false;
  }

  // ARモードの確認
  if (settings.enabledWorldTracking !== undefined && settings.enabledWorldTracking !== null)
    g_AR_Enabled_WorldTracking = settings.enabledWorldTracking;
  if (settings.enabledImageTracking !== undefined && settings.enabledImageTracking !== null)
    g_AR_Enabled_ImageTracking = settings.enabledImageTracking;
  if (settings.enabledVPS !== undefined && settings.enabledVPS !== null)
    g_AR_Enabled_VPS = settings.enabledVPS;
  if (settings.imageTargets !== undefined && settings.imageTargets !== null) {
    g_AR_imageTargets = settings.imageTargets;
  }

  // Scriptロード
  await createScriptTag('https://cdn.8thwall.com/web/xrextras/xrextras.js', 'anonymous');

  const core = `https://apps.8thwall.com/xrweb?appKey=${apiKey}`;
  await createScriptTag(core, 'anonymous');

  return true;
};

export const Do8thwallHitPlane = (x: number, y: number) => {
  const hitTestResults = XR8.XrController.hitTest(x, y, ['FEATURE_POINT']);

  if (hitTestResults.length === 0) return {};

  const position = ConvPosLeftToRight(hitTestResults[0].position);
  const rotation = ConvRotLeftToRight(hitTestResults[0].rotation);
  const distance = hitTestResults[0].distance;

  return {
    position: position,
    rotation: rotation,
    distance: distance
  };
};

export const Do8thwallUpdateConfig = (settings: any): boolean => {
  const arConfig: ARConfig = {};

  const newImageTargets = settings.imageTargets;
  if (newImageTargets !== undefined && newImageTargets !== null) {
    arConfig.imageTargets = newImageTargets;
  }

  // 設定反映
  XR8.XrController.configure(arConfig);

  return true;
};

const createScriptTag = (src: string, crossOrigin: string) => {
  return new Promise((resolve, reject) => {
    const scriptTag = document.createElement('script');
    scriptTag.src = src;
    scriptTag.crossOrigin = crossOrigin;
    scriptTag.onload = () => resolve(scriptTag);
    scriptTag.onerror = () => reject(new Error(`Failed to load script ${src}`));
    document.body.appendChild(scriptTag);
  });
};

const ConvPosLeftToRight = (SrcPos: any) => {
  const DstPos = SrcPos;
  DstPos.z *= -1.0;

  return DstPos;
};

const ConvRotLeftToRight = (SrcRot: any) => {
  const DstRot = SrcRot;
  DstRot.x *= -1.0;
  DstRot.y *= -1.0;

  return DstRot;
};

interface ARConfig {
  disableWorldTracking?: boolean;
  imageTargets?: string[];
  enableVps?: boolean;
}

const Init8thwall = () => {
  // カメラ映像描画用Canvasを生成
  const camerafeed = document.createElement('canvas');
  camerafeed.id = 'camerafeed';
  camerafeed.className = 'camerafeed';
  camerafeed.style.display = 'none';
  document.body.appendChild(camerafeed);

  // 設定
  {
    const arConfig: ARConfig = {};

    // ワールドトラッキング(平面検出を使用しない)
    if (!g_AR_Enabled_WorldTracking) {
      arConfig.disableWorldTracking = true;
    }

    if (g_AR_imageTargets.length !== 0) {
      arConfig.imageTargets = g_AR_imageTargets;
    }

    if (g_AR_Enabled_VPS) {
      arConfig.enableVps = true;
    }

    // 設定反映
    XR8.XrController.configure(arConfig);
  }

  //
  const modules = [
    // カメラ映像をカメラ映像描画用Canvas(camerafeed)に描画するモジュール
    XR8.GlTextureRenderer.pipelineModule(),

    // SLAMのトラッキングを有効にするモジュール
    XR8.XrController.pipelineModule(),

    // 未対応な端末を検出するモジュール
    XRExtras.AlmostThere.pipelineModule(),

    // カメラ映像描画用Canvas(camerafeed)をウィンドウいっぱいにリサイズするモジュール
    XRExtras.FullWindowCanvas.pipelineModule(),

    // 8thwall起動時のローディング画面(規約上表示する必要がある)
    XRExtras.Loading.pipelineModule(),

    // 8thwallのエラーを表示するモジュール
    XRExtras.RuntimeError.pipelineModule(),

    // カメラ処理を受け取るカスタムモジュール
    HELCameraModule()
  ];

  // Image Tracking
  if (g_AR_Enabled_ImageTracking) {
    modules.push(HELImageTrackingModule());
  }

  // VPS
  if (g_AR_Enabled_VPS) {
    modules.push(HELVPSModule());
  }

  XR8.addCameraPipelineModules(modules);

  // 潜在的なバグ回避のためにWebGLバージョンをHeliodorと合わせる
  const useWebGL10 = getWebGL10Toggle();

  // 8thwallパイプラインの開始
  // 引数にはカメラ映像を描画するCanvasを指定
  XR8.run({canvas: camerafeed, webgl2: !useWebGL10});
};

const HELCameraModule = () => {
  return {
    name: 'hel-camera-module',

    onUpdate: ({processCpuResult}: {processCpuResult: any}) => {
      // camerafeedキャンバスをテクスチャとしてHeliodorに渡す
      hel_setText(
        ETextParamType_SceneItem,
        'ReplaceTextureByHTMLElement,' + g_AR_BGItem + ',' + '' + ',' + 'camerafeed'
      );

      // 背景画像用のPlaneを1:1にリサイズする設定(常に画面と同じサイズ)
      hel_setText(
        ETextParamType_SceneItem,
        'SetPlaneCustomRect,' + g_AR_BGItem + ',' + '1' + ',' + '1'
      );

      if (processCpuResult === undefined || processCpuResult === null) return;

      const reality = processCpuResult.reality;
      if (reality === undefined || reality === null) return;

      if (
        reality.position === undefined ||
        reality.position === null ||
        reality.rotation === undefined ||
        reality.rotation === null ||
        reality.intrinsics === undefined ||
        reality.intrinsics === null
      )
        return;

      const position = ConvPosLeftToRight(reality.position);
      const rotation = ConvRotLeftToRight(reality.rotation);
      const intrinsics = reality.intrinsics;

      // カメラ座標
      hel_setText(
        ETextParamType_SceneItem,
        'SetPos,' +
          g_AR_CameraItem +
          ',' +
          position.x.toString() +
          ',' +
          position.y.toString() +
          ',' +
          position.z.toString()
      );

      // カメラ回転
      hel_setText(
        ETextParamType_SceneItem,
        'SetQuaternion,' +
          g_AR_CameraItem +
          ',' +
          rotation.x.toString() +
          ',' +
          rotation.y.toString() +
          ',' +
          rotation.z.toString() +
          ',' +
          rotation.w.toString()
      );

      // プロジェクション行列
      const mainCanvas = document.getElementById('canvas') as HTMLCanvasElement;
      if (mainCanvas !== undefined && mainCanvas !== null) {
        const m22 = intrinsics[10];
        const m32 = intrinsics[14];

        const width = mainCanvas.width;
        const height = mainCanvas.height;
        const near = m32 / (m22 - 1);
        const far = m32 / (m22 + 1);
        const fov = 2.0 * Math.atan(1.0 / intrinsics[5]) * (180.0 / Math.PI);

        hel_setText(
          ETextParamType_SceneItem,
          'SetProjection,' +
            g_AR_CameraItem +
            ',' +
            width.toString() +
            ',' +
            height.toString() +
            ',' +
            near.toString() +
            ',' +
            far.toString() +
            ',' +
            fov.toString()
        );
      }
    }
  };
};

const parseImageDetail = (detail: any, visible: boolean): string => {
  const name = detail.name;
  const position = ConvPosLeftToRight(detail.position);
  const rotation = ConvRotLeftToRight(detail.rotation);
  const scale = {x: detail.scale, y: detail.scale, z: detail.scale};

  const result = {
    name: name,
    position: position,
    rotation: rotation,
    scale: scale,
    visible: visible
  };

  return JSON.stringify(result);
};

const showImageTarget = ({detail}: {detail: any}) => {
  const result = parseImageDetail(detail, true);

  hel_setText(ETextParamType_OnARDetectedImage, result);
};

const hideImageTarget = ({detail}: {detail: any}) => {
  const result = parseImageDetail(detail, false);

  hel_setText(ETextParamType_OnARDetectedImage, result);
};

const HELImageTrackingModule = () => {
  return {
    name: 'hel-image-tracking-module',
    listeners: [
      {event: 'reality.imagefound', process: showImageTarget},
      {event: 'reality.imageupdated', process: showImageTarget},
      {event: 'reality.imagelost', process: hideImageTarget}
    ]
  };
};

const foundWayspot = ({detail}: {detail: any}) => {
  const result = parseVPSDetail(detail, true);

  hel_setText(ETextParamType_OnARFoundWayspot, result);
};

const lostWayspot = ({detail}: {detail: any}) => {
  const result = parseVPSDetail(detail, false);

  hel_setText(ETextParamType_OnARFoundWayspot, result);
};

const parseVPSDetail = (detail: any, visible: boolean): string => {
  const name = detail.name;
  const position = ConvPosLeftToRight(detail.position);
  const rotation = ConvRotLeftToRight(detail.rotation);

  const result = {
    name: name,
    position: position,
    rotation: rotation,
    visible: visible
  };

  return JSON.stringify(result);
};

const HELVPSModule = () => {
  return {
    name: 'hel-vps-module',
    listeners: [
      {event: 'reality.projectwayspotfound', process: foundWayspot},
      {event: 'reality.projectwayspotlost', process: lostWayspot}
    ]
  };
};

window.addEventListener('xrloaded', Init8thwall);
